import styled from '@emotion/styled';
import { UserSidePanel } from '@features/user/templates/UserSidePanel';
import getRoute from '@root/src/services/routeList';
import { NextRouter, useRouter } from 'next/router';
import { ReactNode, useMemo, useState } from 'react';

interface SidePanelComponentProps {
  closeSidePanel: () => void;
  sidePanelIdQuery?: string;
  sidePanelParamOneQuery?: string;
}

enum SidePanelSize {
  REGULAR,
  BIG,
}

interface SidePanelComponentConfig {
  node: (props: SidePanelComponentProps) => ReactNode;
  size: SidePanelSize;
}

export enum SidePanelName {
  USER = 'USER',
}

type SidePanelComponentsType = {
  [key in SidePanelName]: SidePanelComponentConfig;
};

export const SidePanelComponents: SidePanelComponentsType = {
  [SidePanelName.USER]: {
    node: ({ closeSidePanel, sidePanelIdQuery }) => (
      <UserSidePanel closeSidePanel={closeSidePanel} userId={sidePanelIdQuery ? Number(sidePanelIdQuery) : undefined} />
    ),
    size: SidePanelSize.REGULAR,
  },
};

const SidePanelFactory = (): JSX.Element | null => {
  const router = useRouter();

  const [size, setSize] = useState<SidePanelSize | undefined>(undefined);

  const sidePanelQuery = router.query.sidePanel as SidePanelName;
  const sidePanelIdQuery = router.query.sidePanelId as string;
  const sidePanelParamOneQuery = router.query.sidePanelParamOne as string;
  const component = SidePanelComponents[sidePanelQuery];

  // Keep the last opened panel size
  const sidePanelSize = useMemo(() => getSidePanelStyle(size), [size]);

  if (!component) {
    return null;
  }

  const componentRender = component.node({
    closeSidePanel: () => closeSidePanelNow(router),
    sidePanelIdQuery,
    sidePanelParamOneQuery,
  });

  if (size !== component.size) {
    setSize(component.size);
  }

  return <SidePanelContainer width={sidePanelSize.width}>{componentRender}</SidePanelContainer>;
};

export default SidePanelFactory;

const SidePanelContainer = styled.div<{ width: string }>`
  position: fixed;
  top: 0;
  right: 0;
  width: ${props => `${props.width}`};
  height: 100vh;

  overflow-x: hidden;
  z-index: var(--z-index-side-panel);

  background: lightcoral;
  border-left: 1px solid red;
`;

function closeSidePanelNow(router: NextRouter): void {
  router.push(getRoute.closeSidePanel(router), undefined, { scroll: false }).catch(() => {});
}

export function getSidePanelStyle(size?: SidePanelSize): { width: string } {
  switch (size) {
    case SidePanelSize.REGULAR:
      return { width: 'var(--width-side-panel-regular)' };
    case SidePanelSize.BIG:
      return { width: 'var(--width-side-panel-big)' };
    default:
      return { width: 'var(--width-side-panel-regular)' };
  }
}
